import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProductImage } from '../utils/productImages';
import { getArabicTranslation } from '../utils/arabicTranslations';
import api from '../services/api';
import { useHoldTimer } from '../hooks/useHoldTimer';
import './CartSidebar.css';

const CartSidebar = ({ isOpen, onClose }) => {
  const [cartItems, setCartItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const { getItemSeconds, formatTime } = useHoldTimer();

  useEffect(() => {
    const loadCart = () => {
      const cart = localStorage.getItem('cart');
      if (cart) {
        try {
          const parsedCart = JSON.parse(cart);
          setCartItems(parsedCart.items || []);
          setSubtotal(parsedCart.total || 0);
        } catch (e) {
          console.error('Error parsing cart:', e);
          setCartItems([]);
          setSubtotal(0);
        }
      } else {
        setCartItems([]);
        setSubtotal(0);
      }
    };

    // Load cart when sidebar opens
    if (isOpen) {
      loadCart();
    }

    // Listen for cart updates
    const handleCartUpdate = (event) => {
      if (event.detail) {
        setCartItems(event.detail.items || []);
        setSubtotal(event.detail.total || 0);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, [isOpen]);

  const removeItem = (itemId) => {
    const itemToRemove = cartItems.find(item => item.id === itemId);
    
    // Release any backend seat holds (safety net for legacy cart items)
    if (itemToRemove?.type === 'amphitheater' && itemToRemove?.holdIds) {
      itemToRemove.holdIds.forEach(holdId => {
        api.releaseSeatHold(holdId).catch(() => {});
      });
    }
    
    const updatedItems = cartItems.filter(item => item.id !== itemId);
    const newTotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    localStorage.setItem('cart', JSON.stringify({
      items: updatedItems,
      total: newTotal
    }));
    
    setCartItems(updatedItems);
    setSubtotal(newTotal);
    
    window.dispatchEvent(new CustomEvent('cartUpdated', { 
      detail: { items: updatedItems, total: newTotal } 
    }));
  };

  return (
    <>
      <div className={`cart-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="cart-sidebar-header">
          <h3>Cart <span className="arabic-text on-white">{getArabicTranslation('Cart')}</span></h3>
          <button className="close-btn" onClick={onClose} aria-label="Close cart">
            &times;
          </button>
        </div>
        
        <div className="cart-sidebar-content">
          {cartItems.length === 0 ? (
            <div className="cart-empty">
              <p>Your cart is empty</p>
            </div>
          ) : (
            <>
              {cartItems.map((item) => (
                <div key={item.id} className="cart-item">
                  <div className="cart-item-image">
                    <img loading="lazy" src={getProductImage(item)} alt={item.name} />
                  </div>
                  <div className="cart-item-details">
                    <h4>{item.name}</h4>
                    {item.type === 'amphitheater' && (item.section || item.seatDetails) && (
                      <p style={{fontSize: '0.75em', color: '#888', margin: '2px 0 0'}}>
                        {item.section && <span>Section: {item.section}</span>}
                        {item.seatDetails && item.seatDetails.length > 0 && (
                          <>
                            {item.seatDetails.length === 1 ? (
                              <>
                                {item.seatDetails[0].row && <span> | Row: {item.seatDetails[0].row}</span>}
                                {item.seatDetails[0].number && <span> | Seat: {item.seatDetails[0].number}</span>}
                              </>
                            ) : (
                              <span> | {item.seatDetails.length} seats</span>
                            )}
                          </>
                        )}
                      </p>
                    )}
                    <p>{item.quantity} × ${Number(item.price).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    {(() => { const secs = item.type === 'amphitheater' ? getItemSeconds(item.id) : null; return secs !== null && secs > 0 ? (
                      <p style={{fontSize: '0.8em', color: secs < 60 ? '#ef4444' : '#f59e0b', fontWeight: '600', marginTop: '4px'}}>
                        ⏱ Hold expires in {formatTime(secs)}
                      </p>
                    ) : secs === 0 ? (
                      <p style={{fontSize: '0.8em', color: '#ef4444', fontWeight: '600', marginTop: '4px'}}>
                        ⚠ Hold expired — seats released
                      </p>
                    ) : null; })()}
                  </div>
                  <button 
                    className="cart-item-remove"
                    onClick={() => removeItem(item.id)}
                    aria-label="Remove item"
                  >
                    &times;
                  </button>
                </div>
              ))}
              <div className="cart-subtotal">
                <span>Subtotal:</span>
                <span>${subtotal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="cart-subtotal" style={{fontSize: '0.85em', opacity: 0.8}}>
                <span>Tax (7.75%):</span>
                <span>${(subtotal * 0.0775).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="cart-subtotal" style={{fontWeight: 'bold', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '8px', marginTop: '4px'}}>
                <span>Total:</span>
                <span>${(subtotal * 1.0775).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            </>
          )}
        </div>
        
        <div className="cart-sidebar-footer">
          <Link 
            to="/cart" 
            className="view-cart-btn"
            onClick={onClose}
          >
            View Cart
          </Link>
          <Link 
            to="/cart" 
            className="checkout-btn"
            onClick={onClose}
          >
            Checkout
          </Link>
        </div>
      </div>
      <div className={`cart-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}></div>
    </>
  );
};

export default CartSidebar;
